export default class RechargeGateway {
  static rechargeObject;
  static session;
  static MAX_RETRIES = 3;

  static async createRechargeObject() {
    RechargeGateway.rechargeObject = recharge;

    RechargeGateway.session =
      recharge.customerSession ?? (await recharge.auth.loginShopifyAppProxy());
  }

  static async getCustomerId() {
    if (
      !RechargeGateway.rechargeObject ||
      !RechargeGateway?.session?.customerId
    )
      await RechargeGateway.createRechargeObject();
    return RechargeGateway.session.customerId;
  }

  static async getAddresses() {
    if (
      !RechargeGateway.rechargeObject ||
      !RechargeGateway?.session?.customerId
    )
      await RechargeGateway.createRechargeObject();

    let rechargeAddressListObject = {};
    if (RechargeGateway?.session?.customerId) {
      try {
        rechargeAddressListObject =
          await RechargeGateway.rechargeObject.address.listAddresses(
            RechargeGateway.session,
            {}
          );
      } catch (e) {
        console.error(e);
      }
    }

    return rechargeAddressListObject?.addresses ?? [];
  }

  static async getSubscriptions(limit = 250) {
    if (!RechargeGateway.rechargeObject)
      await RechargeGateway.createRechargeObject();

    let rechargeSubscriptionsListObject = {};
    try {
      rechargeSubscriptionsListObject =
        await RechargeGateway.rechargeObject.subscription.listSubscriptions(
          RechargeGateway.session,
          {
            sort_by: 'id-asc',
            status: 'active',
            limit
          }
        );
    } catch (e) {
      console.error(e);
    }

    return rechargeSubscriptionsListObject?.subscriptions ?? [];
  }

  static async getSubscriptionsWithCancelled(limit = 250) {
    if (!RechargeGateway.rechargeObject)
      await RechargeGateway.createRechargeObject();

    let rechargeSubscriptionsListObject = {};
    try {
      rechargeSubscriptionsListObject =
        await RechargeGateway.rechargeObject.subscription.listSubscriptions(
          RechargeGateway.session,
          {
            sort_by: 'id-asc',
            limit
          }
        );
    } catch (e) {
      console.error(e);
    }

    return rechargeSubscriptionsListObject?.subscriptions ?? [];
  }

  static async getSubscriptionsArray(subs) {
    const limit = 250;
    if (!RechargeGateway.rechargeObject)
      await RechargeGateway.createRechargeObject();

    let rechargeSubscriptionsListObject = {};
    try {
      rechargeSubscriptionsListObject =
        await RechargeGateway.rechargeObject.subscription.listSubscriptions(
          RechargeGateway.session,
          {
            sort_by: 'id-asc',
            limit,
            ids: subs
          }
        );
    } catch (e) {
      console.error(e);
    }

    return rechargeSubscriptionsListObject?.subscriptions ?? [];
  }

  static async getAddress(addressId, options = {}) {
    if (!RechargeGateway.rechargeObject)
      await RechargeGateway.createRechargeObject();

    let address = {};
    try {
      address = await RechargeGateway.rechargeObject.address.getAddress(
        RechargeGateway.session,
        addressId,
        options
      );
    } catch (e) {
      console.error(e);
    }

    return address;
  }

  static async updateAddress(addressObject, addressId) {
    if (!RechargeGateway.rechargeObject)
      await RechargeGateway.createRechargeObject();

    const response = await RechargeGateway.rechargeObject.address.updateAddress(
      RechargeGateway.session,
      addressId,
      {
        ...addressObject
      }
    );

    return response;
  }

  static async createAddress(addressObject) {
    if (!RechargeGateway.rechargeObject)
      await RechargeGateway.createRechargeObject();

    const response = await RechargeGateway.rechargeObject.address.createAddress(
      RechargeGateway.session,
      addressObject
    );

    return response;
  }

  static async updateSubscription(
    subscriptionObject,
    subscriptionId,
    retriesCounter = 0
  ) {
    if (retriesCounter >= RechargeGateway.MAX_RETRIES) {
      console.error(
        `Maximum retry attempts reached for updating subscription ${subscriptionId}.`
      );
      return;
    }

    if (!RechargeGateway.rechargeObject)
      await RechargeGateway.createRechargeObject();

    try {
      await RechargeGateway.rechargeObject.subscription.updateSubscription(
        RechargeGateway.session,
        subscriptionId,
        {
          ...subscriptionObject
        }
      );
    } catch (e) {
      console.error(e);
      retriesCounter++;
      await RechargeGateway.updateSubscription(
        subscriptionObject,
        subscriptionId,
        retriesCounter
      );
    }
  }

  static async updateChargeDate(date, subscriptionId, retriesCounter = 0) {
    if (retriesCounter >= RechargeGateway.MAX_RETRIES) {
      console.error(
        `Maximum retry attempts reached for updating the charge date in subscription ${subscriptionId}.`
      );
      return;
    }

    if (!RechargeGateway.rechargeObject)
      await RechargeGateway.createRechargeObject();

    try {
      await RechargeGateway.rechargeObject.subscription.updateSubscriptionChargeDate(
        RechargeGateway.session,
        subscriptionId,
        date
      );
    } catch (e) {
      console.error(e);
      retriesCounter++;
      await this.updateChargeDate(date, subscriptionId, retriesCounter);
    }
  }

  static async deleteAddress(addressId) {
    if (!RechargeGateway.rechargeObject)
      await RechargeGateway.createRechargeObject();

    try {
      await RechargeGateway.rechargeObject.address.deleteAddress(
        RechargeGateway.session,
        addressId
      );
    } catch (e) {
      throw e;
    }
  }

  static async updateManySubscriptions(subscriptions, addressId) {
    if (!RechargeGateway.rechargeObject)
      await RechargeGateway.createRechargeObject();
    try {
      await RechargeGateway.rechargeObject.subscription.updateSubscriptions(
        RechargeGateway.session,
        addressId,
        subscriptions
      );
    } catch (e) {
      throw e;
    }
  }

  static async updateReactiveSubscritions(subscriptions, redirect) {
    if (!RechargeGateway.rechargeObject)
      await RechargeGateway.createRechargeObject();

    async function activateSubscriptions(subscriptions, session) {
      for (const sub of subscriptions) {
        await recharge.subscription.activateSubscription(session, sub);
      }
    }

    if (subscriptions) {
      try {
        const session = await recharge.auth.loginShopifyAppProxy();
        await activateSubscriptions(subscriptions, session);
        if (redirect) {
          window.location.href = '/pages/subscriptions';
        }
      } catch (e) {
        throw e;
      }
    }
  }
}
